import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardActionArea, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FirebaseContext } from 'common';
import PublicLayout from '../components/PublicLayout.jsx';
import { resolveShopContext } from '../common/shopContext';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { formatMoney } from '../common/shopPricing';
import { isPartnerComingSoon, isPartnerMaintenance } from '../common/partnerUtils';
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import { getLocalizedShopItem } from '../common/shopContent';

const parseMs = (value) => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

const formatDateShort = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(value));
  } catch (e) {
    return String(value);
  }
};

const getAuctionStatus = (auction, nowMs) => {
  const startMs = parseMs(auction?.startsAt);
  const endMs = parseMs(auction?.endsAt);
  if (startMs && nowMs < startMs) return 'upcoming';
  if (endMs && nowMs > endMs) return 'ended';
  return 'live';
};

export default function Auctions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerSlug } = useParams();
  const firebase = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();

  const shopContext = useMemo(
    () =>
      resolveShopContext(settings, {
        partnerSlug,
        locale: i18n.language,
        pathname: location.pathname,
      }),
    [settings, partnerSlug, i18n.language, location.pathname]
  );

  const { partner, partnerKey, paths, websiteContent } = shopContext;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';
  const gateInactive = Boolean(partner?.inactive);
  const gateComingSoon = Boolean(partner && isPartnerComingSoon(partner) && !isAdmin);
  const gateMaintenance = Boolean(partner && isPartnerMaintenance(partner) && !isAdmin);

  const templateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const theme = getSiteTheme(templateKey, websiteContent.appearance || {});

  const shop = websiteContent.shop || {};
  const shopEnabled = shop.enabled !== false;
  const currency = shop.currency || 'USD';

  const auctions = useMemo(() => {
    const list = Array.isArray(shop.auctions) ? shop.auctions : [];
    return list
      .filter((a) => a && a.published !== false)
      .map((a) => getLocalizedShopItem(a, i18n.language));
  }, [shop.auctions, i18n.language]);

  const [states, setStates] = useState({});
  useEffect(() => {
    if (gateInactive || gateComingSoon || gateMaintenance) {
      setStates({});
      return;
    }
    if (!firebase?.database) return;
    const ref = firebase.database.ref(`shop_auction_state/${partnerKey || 'global'}`);
    ref.on('value', (snap) => setStates(snap.val() || {}));
    return () => ref.off();
  }, [firebase?.database, partnerKey, gateInactive, gateComingSoon, gateMaintenance]);

  const nowMs = Date.now();
  const byStatus = useMemo(() => {
    const live = [];
    const upcoming = [];
    const ended = [];
    auctions.forEach((auction) => {
      const status = getAuctionStatus(auction, nowMs);
      if (status === 'upcoming') upcoming.push(auction);
      else if (status === 'ended') ended.push(auction);
      else live.push(auction);
    });
    live.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    upcoming.sort((a, b) => (parseMs(a.startsAt) || 0) - (parseMs(b.startsAt) || 0));
    ended.sort((a, b) => (parseMs(b.endsAt) || 0) - (parseMs(a.endsAt) || 0));
    return { live, upcoming, ended };
  }, [auctions, nowMs]);

  if (gateInactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (gateComingSoon) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (gateMaintenance) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  const renderAuctionCard = (auction) => {
    const status = getAuctionStatus(auction, nowMs);
    const state = auction?.id ? states?.[auction.id] : null;
    const highestBid = Number(state?.highestBid || 0);
    const bidCount = Number(state?.bidCount || 0);
    const startingBid = Number(auction?.startingBid || 0);
    const displayBid = highestBid > 0 ? highestBid : startingBid;

    const chip =
      status === 'live' ? (
        <Chip label={t('live') || 'Live'} color="success" size="small" sx={{ fontWeight: 900 }} />
      ) : status === 'upcoming' ? (
        <Chip label={t('upcoming') || 'Upcoming'} size="small" sx={{ fontWeight: 900 }} />
      ) : (
        <Chip label={t('ended') || 'Ended'} size="small" sx={{ fontWeight: 900 }} />
      );

    return (
      <Card
        key={auction.id || auction.title}
        variant="outlined"
        sx={{ borderRadius: 3, borderColor: theme.border, bgcolor: theme.cardBg, height: '100%' }}
      >
        <CardActionArea
          onClick={() => navigate(`${paths.auctions}/${encodeURIComponent(auction.id)}`)}
          sx={{ height: '100%' }}
        >
          <Box
            sx={{
              height: 180,
              position: 'relative',
              bgcolor: theme.sectionAltBg,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {auction.image ? (
              <Box component="img" src={auction.image} alt={auction.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'radial-gradient(900px 260px at 20% 20%, rgba(250,204,21,0.25), transparent 60%), radial-gradient(800px 260px at 80% 30%, rgba(34,197,94,0.20), transparent 60%), linear-gradient(135deg, #0b1120 0%, #111827 60%)',
                }}
              />
            )}
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.15)' }} />
            <Box sx={{ position: 'absolute', top: 12, left: 12 }}>{chip}</Box>
            <Box sx={{ position: 'absolute', bottom: 12, left: 12, right: 12 }}>
              <Typography sx={{ fontWeight: 950, color: '#fff', fontSize: 16 }}>
                {auction.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>
                {(t('current_bid') || 'Current bid') + ': ' + formatMoney(displayBid, currency)}
                {bidCount ? ` · ${bidCount} ${t('bids') || 'bids'}` : ''}
              </Typography>
            </Box>
          </Box>

          <CardContent>
            {auction.description ? (
              <Typography variant="body2" sx={{ color: theme.muted, minHeight: 42 }}>
                {auction.description}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
              <GavelRoundedIcon fontSize="small" />
              <Typography variant="body2" sx={{ color: theme.muted }}>
                {status === 'upcoming' && auction.startsAt
                  ? (t('starts') || 'Starts') + ' ' + formatDateShort(auction.startsAt)
                  : status === 'ended' && auction.endsAt
                    ? (t('ended') || 'Ended') + ' ' + formatDateShort(auction.endsAt)
                    : auction.endsAt
                      ? (t('ends') || 'Ends') + ' ' + formatDateShort(auction.endsAt)
                      : (t('live') || 'Live')}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <PublicLayout title={t('auctions') || 'Auctions'} partner={partner} homePath={paths.home} bookingPath={paths.booking}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 6, md: 8 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: { xs: 32, md: 44 }, letterSpacing: -1 }}>
              {t('auctions') || 'Auctions'}
            </Typography>
            <Typography sx={{ color: theme.muted, mt: 1, maxWidth: 680 }}>
              {t('auctions_subtitle') || 'Bid on limited drops and special releases.'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate(paths.shop)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
          >
            {t('back_to_shop') || 'Back to shop'}
          </Button>
        </Stack>

        {!shopEnabled ? (
          <Alert severity="info" sx={{ mt: 3 }}>
            {t('shop_disabled_help') || 'Please check back later.'}
          </Alert>
        ) : !auctions.length ? (
          <Box sx={{ mt: 4, p: 3, borderRadius: 2, border: `1px solid ${theme.border}`, bgcolor: theme.sectionAltBg }}>
            <Typography sx={{ fontWeight: 900, color: theme.text }}>
              {t('no_auctions') || 'No auctions yet'}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.muted, mt: 0.5 }}>
              {t('no_auctions_help') || 'Ask an admin to publish auctions in Storefront > Shop > Auctions.'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3.5} sx={{ mt: 3 }}>
            {byStatus.live.length ? (
              <Box>
                <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18, mb: 1.5 }}>
                  {t('live') || 'Live'}
                </Typography>
                <Grid container spacing={2.5}>
                  {byStatus.live.map((auction) => (
                    <Grid key={auction.id} item xs={12} md={4}>
                      {renderAuctionCard(auction)}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : null}

            {byStatus.upcoming.length ? (
              <Box>
                <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18, mb: 1.5 }}>
                  {t('upcoming') || 'Upcoming'}
                </Typography>
                <Grid container spacing={2.5}>
                  {byStatus.upcoming.map((auction) => (
                    <Grid key={auction.id} item xs={12} md={4}>
                      {renderAuctionCard(auction)}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : null}

            {byStatus.ended.length ? (
              <Box>
                <Typography sx={{ fontWeight: 950, color: theme.text, fontSize: 18, mb: 1.5 }}>
                  {t('ended') || 'Ended'}
                </Typography>
                <Grid container spacing={2.5}>
                  {byStatus.ended.map((auction) => (
                    <Grid key={auction.id} item xs={12} md={4}>
                      {renderAuctionCard(auction)}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : null}
          </Stack>
        )}
      </Box>
    </PublicLayout>
  );
}
